import { z } from 'zod';

/**
 * Single error-code registry (Master Prompt §57, §89).
 * Every API error uses one of these codes. `message` is Persian and safe to show to users.
 * Adding a code is fine; renaming or removing one is a breaking API change.
 */
export interface ErrorDefinition {
  readonly status: number;
  readonly message: string;
}

export const ERROR_CODES = {
  // generic
  VALIDATION_FAILED: { status: 400, message: 'اطلاعات ارسالی معتبر نیست.' },
  NOT_FOUND: { status: 404, message: 'موردی پیدا نشد.' },
  PAYLOAD_TOO_LARGE: { status: 413, message: 'حجم درخواست بیش از حد مجاز است.' },
  UNSUPPORTED_MEDIA_TYPE: { status: 415, message: 'قالب درخواست پشتیبانی نمی‌شود.' },
  RATE_LIMITED: { status: 429, message: 'تعداد درخواست‌ها زیاد است. کمی بعد دوباره تلاش کنید.' },
  CONFLICT: { status: 409, message: 'این کار با وضعیت فعلی داده سازگار نیست. صفحه را تازه کنید.' },
  INTERNAL_ERROR: { status: 500, message: 'خطای غیرمنتظره‌ای رخ داد. دوباره تلاش کنید.' },
  SERVICE_UNAVAILABLE: { status: 503, message: 'سرویس موقتاً در دسترس نیست.' },

  // authentication
  AUTH_REQUIRED: { status: 401, message: 'برای ادامه وارد شوید.' },
  AUTH_TOKEN_INVALID: { status: 401, message: 'نشست شما معتبر نیست. دوباره وارد شوید.' },
  AUTH_SESSION_REVOKED: { status: 401, message: 'نشست شما پایان یافته است. دوباره وارد شوید.' },
  AUTH_REFRESH_INVALID: { status: 401, message: 'نشست شما منقضی شده است. دوباره وارد شوید.' },
  AUTH_USER_DISABLED: { status: 403, message: 'حساب کاربری شما غیرفعال است.' },
  AUTH_INVALID_OTP: { status: 400, message: 'کد واردشده درست نیست.' },
  AUTH_OTP_EXPIRED: { status: 400, message: 'کد منقضی شده است. کد تازه بگیرید.' },
  AUTH_OTP_ATTEMPTS_EXCEEDED: {
    status: 400,
    message: 'تعداد تلاش‌ها بیش از حد مجاز است. کد تازه بگیرید.',
  },
  AUTH_RATE_LIMITED: {
    status: 429,
    message: 'تعداد تلاش‌ها بیش از حد مجاز است. کمی بعد دوباره تلاش کنید.',
  },
  REQUEST_ORIGIN_REJECTED: { status: 403, message: 'این درخواست از مبدأ مجاز ارسال نشده است.' },
  // password login (ADR-033)
  AUTH_INVALID_CREDENTIALS: { status: 400, message: 'شماره‌ی موبایل یا رمز درست نیست.' },
  AUTH_PASSWORD_INCORRECT: { status: 400, message: 'رمز درست نیست.' },
  AUTH_PASSWORD_WEAK: {
    status: 400,
    message: 'رمز نباید شماره‌ی موبایل شما را داشته باشد. رمز دیگری انتخاب کنید.',
  },
  AUTH_METHOD_DISABLED: { status: 409, message: 'ورود با کد پیامکی فعلاً فعال نیست. با رمز وارد شوید.' },
  PASSWORD_LINK_INVALID: {
    status: 400,
    message: 'این لینک معتبر نیست یا قبلاً استفاده شده است. از مدیر کلینیک لینک تازه بخواهید.',
  },
  PASSWORD_LINK_EXPIRED: { status: 410, message: 'مهلت این لینک گذشته است. از مدیر کلینیک لینک تازه بخواهید.' },
  PASSWORD_LINK_NOT_ALLOWED: {
    status: 409,
    message: 'این شخص عضو سازمان دیگری هم هست؛ رمز او را فقط پشتیبانی پلتفرم بازنشانی می‌کند.',
  },
  PASSWORD_LINK_SELF: { status: 409, message: 'رمز خودتان را از «تغییر رمز» عوض کنید.' },
  // Deleting a clinic (ADR-046)
  CLINIC_NOT_SUSPENDED: { status: 409, message: 'فقط کلینیکِ تعلیق‌شده حذف می‌شود؛ اول آن را تعلیق کنید.' },
  CLINIC_NAME_MISMATCH: { status: 422, message: 'نام کلینیک را دقیقاً همان‌طور که نوشته شده بنویسید.' },
  CLINIC_EXPORT_REQUIRED: {
    status: 409,
    message: 'پیش از حذف، خروجی کامل این کلینیک را بگیرید (در ۲۴ ساعت گذشته).',
  },
  ORG_CREATION_NOT_ALLOWED: {
    status: 403,
    message: 'ساختن کلینیک تازه فعلاً فقط با معرفی پشتیبانی پلتفرم ممکن است.',
  },

  // tenancy & authorization
  TENANT_CONTEXT_REQUIRED: { status: 400, message: 'سازمان فعال مشخص نشده است.' },
  TENANT_ACCESS_DENIED: { status: 403, message: 'به این سازمان دسترسی ندارید.' },
  PERMISSION_DENIED: { status: 403, message: 'اجازه‌ی انجام این کار را ندارید.' },
  MEMBERSHIP_ALREADY_EXISTS: { status: 409, message: 'این کاربر از قبل عضو این سازمان است.' },
  MEMBERSHIP_ROLE_NOT_ALLOWED: { status: 400, message: 'این نقش برای این نوع سازمان مجاز نیست.' },
  MEMBERSHIP_LAST_ADMIN: { status: 409, message: 'سازمان باید دست‌کم یک مدیر فعال داشته باشد.' },
  // sections (ADR-036)
  SECTION_NOT_ALLOWED: { status: 403, message: 'این کار در این بخش برای شما مجاز نیست.' },
  SECTION_MISMATCH: { status: 422, message: 'این خدمت از بخش دیگری است و به این ویزیت یا نوبت اضافه نمی‌شود.' },
  // ADR-043: the clinic turned its laser section off.
  LASER_DISABLED: { status: 409, message: 'مرکز لیزر در تنظیمات خاموش است؛ اول آن را روشن کنید.' },
  SERVICE_SECTION_IN_USE: {
    status: 409,
    message: 'این خدمت نوبت باز یا پکیج فعال دارد؛ بخش آن را عوض نکنید یا خدمت تازه بسازید.',
  },
  // ADR-042
  MEMBERSHIP_SELF_ACCESS: { status: 409, message: 'دسترسی خودتان را نمی‌توانید عوض کنید. از مدیر دیگری بخواهید.' },
  MEMBERSHIP_ACCESS_CLINIC_ONLY: { status: 400, message: 'دسترسی با تیک فقط برای اعضای کلینیک است.' },
  MEMBERSHIP_SELF_REMOVAL: {
    status: 409,
    message: 'نمی‌توانید عضویت خودتان را حذف کنید. از مدیر دیگری بخواهید.',
  },

  // Clinic Core
  NAME_TAKEN: { status: 409, message: 'این نام قبلاً استفاده شده است. نام دیگری انتخاب کنید.' },
  PATIENT_ARCHIVED: { status: 409, message: 'این کلاینت بایگانی شده است. ابتدا او را از بایگانی خارج کنید.' },
  SERVICE_ARCHIVED: { status: 409, message: 'این خدمت بایگانی شده است.' },
  RESOURCE_ARCHIVED: { status: 409, message: 'این اتاق یا دستگاه بایگانی شده است.' },
  PRACTITIONER_NOT_ELIGIBLE: {
    status: 400,
    message: 'درمانگر باید عضو فعال با نقش پزشک باشد؛ اپراتور لیزر فقط برای خدمت لیزر.',
  },
  RESOURCE_UNAVAILABLE: {
    status: 409,
    message: 'این اتاق یا دستگاه در این زمان در اختیار نوبت دیگری است.',
  },
  RESOURCE_REQUIREMENT_UNMET: {
    status: 400,
    message: 'این خدمت به اتاق یا دستگاه مشخصی نیاز دارد. آن را انتخاب کنید.',
  },
  APPOINTMENT_INVALID_TRANSITION: { status: 409, message: 'این کار در وضعیت فعلی نوبت ممکن نیست.' },
  CHECK_IN_REQUIRED: { status: 409, message: 'پیش از شروع ویزیت، کلاینت را پذیرش کنید.' },
  WALK_IN_NO_TIME: { status: 409, message: 'امروز وقت خالی برای این پزشک نمانده است. نوبت روز دیگری بدهید.' },
  APPOINTMENT_NOT_EDITABLE: {
    status: 409,
    message: 'نوبت فقط پیش از پذیرش قابل جابه‌جایی است.',
  },
  VISIT_NOT_OPEN: { status: 409, message: 'این ویزیت بسته شده است و تغییر نمی‌کند.' },
  VISIT_EMPTY: { status: 409, message: 'برای تکمیل ویزیت دست‌کم یک خدمت ثبت کنید.' },
  PATIENT_NATIONAL_CODE_TAKEN: {
    status: 409,
    message: 'کلاینت دیگری با این کد ملی در این کلینیک ثبت شده است.',
  },
  PATIENT_FILE_NUMBER_TAKEN: {
    status: 409,
    message: 'کلاینت دیگری با این شماره‌ی پرونده در این کلینیک ثبت شده است.',
  },
  SMS_OPERATOR_NOT_ENABLED: {
    status: 409,
    message: 'ارسال از پنل پیامکی هنوز برای این کلینیک فعال نشده است.',
  },
  SMS_SEND_FAILED: { status: 502, message: 'ارسال پیامک انجام نشد. کمی بعد دوباره تلاش کنید.' },

  // Clinic Inventory
  ITEM_ARCHIVED: { status: 409, message: 'این کالا بایگانی شده است.' },
  RECEIPT_COST_RECORDED: { status: 409, message: 'قیمت خرید این قلم پیش‌تر ثبت شده است.' },
  GTIN_TAKEN: { status: 409, message: 'این بارکد به کالای دیگری وصل است.' },
  OPENING_STOCK_NO_COST: { status: 422, message: 'موجودی اولیه خرید نیست و قیمت خرید یا نسیه نمی‌گیرد.' },

  // Central GTIN catalog (ADR-055), stock insights (ADR-056), and default items (ADR-057)
  CATALOG_GTIN_TAKEN: { status: 409, message: 'این GTIN در بانک مرکزی به کالای دیگری وصل است.' },
  CATALOG_REVIEW_CLOSED: { status: 409, message: 'برای این GTIN پیشنهاد منتظری نیست؛ صفحه را تازه کنید.' },
  CATALOG_PRODUCT_RETIRED: { status: 409, message: 'این کالای استاندارد بایگانی شده است.' },
  CATALOG_MERGE_INVALID: { status: 409, message: 'این دو کالا را نمی‌توان یکی کرد (یکی هستند یا یکی بایگانی شده است).' },
  DEFAULT_ITEM_DUPLICATE: { status: 409, message: 'این نام در فهرست پیش‌فرض هست.' },
  DEFAULT_ITEM_IS_PRODUCT: { status: 409, message: 'این ردیف کالای بانک GTIN است؛ از بانک GTIN ویرایشش کنید.' },
  INSIGHTS_NOT_SHARED: { status: 409, message: 'جزئیات انبار این کلینیک به اشتراک گذاشته نشده است.' },
  BATCH_EXPIRED: { status: 409, message: 'این بچ منقضی شده است و مصرف نمی‌شود. آن را از انبار خارج کنید.' },
  BATCH_RESERVED: { status: 409, message: 'این ویال باز برای کلاینت دیگری کنار گذاشته شده است.' },
  STOCK_UNIT_DUPLICATE: {
    status: 409,
    message: 'این برچسب اصالت قبلاً ثبت شده است؛ احتمال تکرار یا جعل را بررسی کنید.',
  },
  STOCK_UNIT_UNKNOWN: { status: 409, message: 'این برچسب اصالت در این بچ ثبت نشده یا قبلاً مصرف شده است.' },

  // Clinic Finance
  PAYMENT_EXCEEDS_BALANCE: { status: 409, message: 'این مبلغ از مانده‌ی بدهی کلاینت بیشتر است.' },
  REFUND_EXCEEDS_CREDIT: { status: 409, message: 'این مبلغ از بستانکاری کلاینت بیشتر است.' },
  ALREADY_REVERSED: { status: 409, message: 'این ثبت قبلاً برگشت خورده است.' },
  PROVIDER_NOT_USABLE: {
    status: 409,
    message: 'این سرویس پرداخت بایگانی شده است و برای دریافت تازه انتخاب نمی‌شود.',
  },
  PROVIDER_TERMS_CHANGED: {
    status: 409,
    message: 'شرایط این سرویس پرداخت همین حالا عوض شد. شرایط تازه را ببینید و دوباره تأیید کنید.',
  },
  PROVIDER_FEE_TOO_HIGH: {
    status: 422,
    message: 'کارمزد این سرویس برابر یا بیشتر از این مبلغ است؛ این دریافت ثبت نمی‌شود.',
  },
  PROVIDER_REFERENCE_TAKEN: {
    status: 409,
    message: 'این شماره‌ی پیگیری برای همین سرویس قبلاً ثبت شده است.',
  },
  PROVIDER_CORRECTION_INVALID: {
    status: 409,
    message: 'ثبت اصلاحی فقط برای دریافت برگشت‌خورده‌ی همین کلاینت و همین سرویس است.',
  },
  PROVIDER_RECEIPT_HAS_SETTLEMENTS: {
    status: 409,
    message: 'برای این دریافت وصول ثبت شده است؛ اول آن وصول را برگشت بزنید.',
  },
  PROVIDER_ENTRY_OWN_COMMAND: {
    status: 409,
    message: 'این ثبت از صفحه‌ی مطالبات سرویس‌های پرداخت برگشت داده می‌شود.',
  },
  PROVIDER_SETTLEMENT_INVALID: {
    status: 422,
    message: 'جمع تخصیص‌ها باید برابر مبلغ واریز باشد و هر طلب باز، از همین سرویس و همین بخش.',
  },
  PROVIDER_ALLOCATION_EXCEEDS: {
    status: 409,
    message: 'مبلغ تخصیص از مانده‌ی این طلب بیشتر است.',
  },
  PROVIDER_SETTLEMENT_DATE_INVALID: {
    status: 422,
    message: 'تاریخ واریز نمی‌تواند در آینده یا پیش از روز دریافت باشد.',
  },
  PROVIDER_DUE_CHANGE_NOT_ALLOWED: {
    status: 409,
    message: 'موعد طلب وصول‌شده یا برگشت‌خورده عوض نمی‌شود.',
  },
  PROVIDER_DUE_INVALID: {
    status: 422,
    message: 'موعد تازه نمی‌تواند پیش از روز دریافت باشد.',
  },
  REVERSAL_NOT_ALLOWED: { status: 409, message: 'برگشتِ یک برگشت ممکن نیست؛ در صورت نیاز ثبت تازه بزنید.' },
  OPENING_BALANCE_EXISTS: { status: 409, message: 'مانده‌ی افتتاحیه‌ی این کلاینت قبلاً ثبت شده است.' },
  DISCOUNT_EXCEEDS_CHARGE: { status: 409, message: 'تخفیف از مبلغ این ویزیت یا بدهی کلاینت بیشتر است.' },
  VISIT_PLAN_INVALID: { status: 422, message: 'اقساط با مانده‌ی این کلاینت یا تاریخ‌ها نمی‌خواند.' },
  VISIT_PLAN_EXISTS: { status: 409, message: 'برای این ویزیت برنامه‌ی اقساط ثبت شده است.' },
  VISIT_NOT_COMPLETED: { status: 409, message: 'فقط ویزیت تکمیل‌شده ابطال مالی می‌شود.' },
  // ADR-052: renewal and touch-up dates
  FOLLOW_UP_VISIT_OPEN: {
    status: 409,
    message: 'موعد تمدید و ترمیم هنگام تکمیل ویزیت انتخاب می‌شود و پس از آن تغییر می‌کند.',
  },
  PRIOR_SERVICE_VOIDED: { status: 409, message: 'این خدمت قبلی ابطال شده است و تغییر نمی‌کند.' },
  PACKAGE_NOT_ACTIVE: { status: 409, message: 'این پکیج فعال نیست.' },
  // Package terms (ADR-037)
  PACKAGE_SECTION_MIXED: { status: 422, message: 'همه‌ی خدمت‌های یک پکیج باید از یک بخش باشند.' },
  PACKAGE_DISCOUNT_TOO_HIGH: { status: 422, message: 'تخفیف از سقف مجاز این پکیج بیشتر است.' },
  PACKAGE_UPFRONT_REQUIRED: { status: 422, message: 'این پکیج باید هنگام فروش کامل پرداخت شود.' },
  PACKAGE_INSTALLMENTS_INVALID: { status: 422, message: 'پیش‌پرداخت یا اقساط با شرایط این پکیج نمی‌خواند.' },
  // Suppliers (ADR-038)
  VENDOR_PAYMENT_EXCEEDS_BALANCE: { status: 409, message: 'این مبلغ از بدهی کلینیک به این تأمین‌کننده بیشتر است.' },
  VENDOR_HAS_BALANCE: { status: 409, message: 'تا بدهی به این تأمین‌کننده صاف نشده، بایگانی نمی‌شود.' },
  VENDOR_ARCHIVED: { status: 409, message: 'این تأمین‌کننده بایگانی شده است.' },
  VENDOR_INSTALLMENTS_INVALID: { status: 422, message: 'جمع اقساط باید برابر مبلغ باشد و تاریخ‌ها به ترتیب.' },
  VENDOR_OPENING_EXISTS: { status: 409, message: 'مانده‌ی افتتاحیه‌ی این تأمین‌کننده قبلاً ثبت شده است.' },
  PACKAGE_EXTENSION_NOT_ALLOWED: { status: 422, message: 'این پکیج تا این تاریخ تمدید نمی‌شود.' },

  // Migration Tools
  REAUTH_REQUIRED: { status: 403, message: 'برای این کار، دوباره هویت خود را تأیید کنید.' },
  IMPORT_NOTHING_TO_IMPORT: {
    status: 409,
    message: 'در این فایل کلاینت تازه‌ای برای ورود نیست؛ همه تکراری یا نامعتبرند.',
  },
  EXPORT_IN_PROGRESS: { status: 409, message: 'یک خروجی در حال ساخت است. تا آماده شدنش صبر کنید.' },
  EXPORT_NOT_READY: { status: 409, message: 'این خروجی هنوز آماده نیست.' },
  EXPORT_EXPIRED: { status: 410, message: 'مهلت دانلود این خروجی گذشته است. خروجی تازه بسازید.' },
  EXPORT_TOO_LARGE: { status: 422, message: 'حجم داده بیش از سقف خروجی است.' },
  EXPORT_FAILED: { status: 500, message: 'ساختن خروجی ناموفق بود. دوباره تلاش کنید.' },
  EXPORT_INTERRUPTED: { status: 409, message: 'ساختن خروجی نیمه‌کاره ماند. خروجی تازه بسازید.' },

  // reserved for later phases (Master Prompt §89); defined now so the registry stays single-sourced
  IDEMPOTENCY_KEY_REQUIRED: { status: 400, message: 'شناسه‌ی یکتای درخواست (Idempotency-Key) لازم است.' },
  IDEMPOTENCY_KEY_REUSED: {
    status: 409,
    message: 'این شناسه‌ی یکتا قبلاً برای درخواست دیگری استفاده شده است.',
  },
  APPOINTMENT_OVERLAP: { status: 409, message: 'این زمان با نوبت دیگری تداخل دارد.' },
  OUT_OF_STOCK: { status: 409, message: 'موجودی کافی نیست.' },
  RESERVATION_EXPIRED: { status: 409, message: 'مهلت رزرو تمام شده است.' },
  PAYMENT_VERIFY_FAILED: { status: 422, message: 'پرداخت تأیید نشد.' },
  PAYMENT_ALREADY_SUCCEEDED: { status: 409, message: 'این سفارش قبلاً پرداخت شده است.' },
  ORDER_NOT_CANCELLABLE: { status: 409, message: 'این سفارش دیگر قابل لغو نیست.' },
  REFUND_NOT_ALLOWED: { status: 409, message: 'بازپرداخت برای این مورد مجاز نیست.' },
  SETTLEMENT_NOT_AVAILABLE: { status: 409, message: 'این مبلغ هنوز قابل تسویه نیست.' },
} as const satisfies Record<string, ErrorDefinition>;

export type ErrorCode = keyof typeof ERROR_CODES;

export const ERROR_CODE_LIST = Object.keys(ERROR_CODES) as ErrorCode[];

export function isErrorCode(value: string): value is ErrorCode {
  return Object.hasOwn(ERROR_CODES, value);
}

export const PROBLEM_TYPE_PREFIX = 'urn:clinic-platform:problem:';

export function problemTypeFor(code: ErrorCode): string {
  return `${PROBLEM_TYPE_PREFIX}${code}`;
}

/** RFC 9457-style problem body returned by every failing API call. */
export const ProblemDetailsSchema = z.object({
  type: z.string(),
  code: z.string(),
  status: z.number().int(),
  message: z.string(),
  details: z.record(z.string(), z.unknown()),
  traceId: z.string(),
});

export type ProblemDetails = z.infer<typeof ProblemDetailsSchema>;
